import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';

import { api, setAuthToken, ApiError } from '@/lib/api';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import type { ProfileSlug, ResolvedPermissions } from '@/lib/admin-types';

/**
 * AuthContext — gerencia estado de autenticação do app.
 *
 * Rodada Frente A (2026-05-17) refatorou o fake R0 pro fluxo real contra o
 * backend `IGM_App_v2/server` (porta 3002).
 *
 * Fluxos:
 *   - `loginWithGoogle(credential)` → POST /api/v1/auth/google-login (skeleton
 *     no backend até Matheus configurar OAuth — retorna 501)
 *   - `loginDev({email, nome})` → POST /api/v1/auth/dev-login (dev-only)
 *   - `logout()` → POST /api/v1/auth/logout + limpa tokens
 *   - useEffect inicial → se houver JWT no localStorage, GET /api/v1/auth/me
 *     pra validar e popular user
 *
 * Departamentos chegam do backend via `me.departamentos` (array de
 * {departmentSlug, role}). Exposto no User como `departments` (lista de slugs)
 * pra compat com chamadores antigos.
 */

interface User {
  id: string;
  name: string;
  email: string;
  isAdmin: boolean;
  fotoUrl: string | null;
  /**
   * FEAT-V2-035 / BUG-V2-047: permissão pra arquivar/inativar cards do Kanban.
   * Admin sempre true (Migration 051 backfill). Demais users só se admin
   * marcou explicitamente no UserFormDialog.
   */
  podeInativarCards: boolean;
  departments: string[]; // só os slugs — detalhe (role) disponível em useUser().departamentos
  departamentos: Array<{ departmentSlug: string; role: 'lider' | 'colaborador' | 'visualizador' }>;
  /** FE2: slug do link pessoal de captação (/f/<slug>). Null se o user não tem. */
  slug: string | null;
  /**
   * FEAT-V2-037 Fase A (Cowork 2026-05-25): perfil de uso resolvido.
   * NULL pra super-admin único (matheus.mussi) — fora da hierarquia.
   * Demais users SEMPRE têm profile preenchido.
   */
  profile: { id: string; slug: ProfileSlug; nome: string; descricao: string | null } | null;
  /** FEAT-V2-037: permissões granulares já resolvidas (super-admin → tudo 1). */
  permissions: ResolvedPermissions;
  /** FEAT-V2-037: shortcut `isAdmin && profile === null`. */
  isSuperAdmin: boolean;
  /** RH-MENU (R1): líder de algum departamento — decidido no servidor. */
  ehLider: boolean;
  /**
   * RH-MENU (R1): pode abrir o RH (diretoria, "Cuida do RH" ou líder; a conta
   * compartilhada não). Decidido no servidor; o menu ainda depende da flag.
   */
  podeAbrirRh: boolean;
  /**
   * 25/10/2026: 'tecnico' = entrou com e-mail e senha e só abre o aplicativo do técnico (`/campo`). O servidor já barra o
   * resto; a tela só evita levá-lo a uma página que responderia "sem acesso".
   */
  tipo: 'interno' | 'cliente' | 'tecnico';
}

interface AuthApiUser {
  id: string;
  email: string;
  nome: string;
  fotoUrl: string | null;
  isAdmin: boolean;
  ativo: boolean;
  // BUG-V2-047: exposto pelo /auth/me a partir desta rodada. Campo opcional
  // por compat com deploy intermediário (frontend novo + backend velho).
  podeInativarCards?: boolean;
  // FE2: slug do link de captação. Opcional por compat com backend velho.
  slug?: string | null;
  // RH-MENU: opcionais por compat com backend velho (sem eles, nada de RH aparece).
  ehLider?: boolean;
  podeAbrirRh?: boolean;
  tipo?: 'interno' | 'cliente' | 'tecnico';
}

/** A sessão que o servidor devolve no login (Google, dev ou o do técnico de campo). */
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthApiUser;
}

interface MeResponse {
  user: AuthApiUser;
  departamentos: Array<{ departmentSlug: string; role: 'lider' | 'colaborador' | 'visualizador' }>;
  // FEAT-V2-037 Fase A: novos campos. Backend pré-Sprint 2 não envia — campos
  // marcados opcional pra compat com deploy intermediário.
  profile?: { id: string; slug: ProfileSlug; nome: string; descricao: string | null } | null;
  permissions?: ResolvedPermissions;
  isSuperAdmin?: boolean;
  config: { googleConfigured: boolean; devLoginEnabled: boolean };
}

interface AuthConfig {
  googleConfigured: boolean;
  devLoginEnabled: boolean;
}

interface AuthContextValue {
  user: User | null;
  config: AuthConfig | null;
  isAuthenticated: boolean;
  loading: boolean;
  loginDev: (input: { email: string; nome?: string }) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  /** O técnico de campo: a rota de entrar (ou de definir a senha) já devolveu a sessão; aqui ela é guardada. */
  entrarComSessao: (sessao: LoginResponse) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'igm_jwt';
const REFRESH_KEY = 'igm_refresh';

function adapt(me: MeResponse): User {
  const apiUser = me.user;
  const deps = me.departamentos;
  // FEAT-V2-037 Fase A: defaults defensivos pra backend pre-Sprint-2 que
  // não envia profile/permissions/isSuperAdmin. UI cai num estado seguro:
  // admin antigo continua vendo botões (isAdmin=true cobre); non-admin cai
  // na semântica conservadora (vê tudo é o default histórico = true).
  const fallbackPermissions: ResolvedPermissions = {
    pode_ver_tarefas_outros: 1,
    pode_inativar_cards: apiUser.isAdmin ? 1 : 0,
    pode_criar_tarefa_pra_outros: apiUser.isAdmin ? 1 : 0,
  };
  return {
    id: apiUser.id,
    name: apiUser.nome,
    email: apiUser.email,
    isAdmin: apiUser.isAdmin,
    fotoUrl: apiUser.fotoUrl,
    // BUG-V2-047: backend pre-rodada não envia o campo → admin true por
    // backfill semântico, demais false (fail-safe — UI não mostra botão
    // que vai 403 na primeira tentativa).
    podeInativarCards: apiUser.podeInativarCards ?? apiUser.isAdmin,
    departments: deps.map((d) => d.departmentSlug),
    departamentos: deps,
    slug: apiUser.slug ?? null,
    // FEAT-V2-037: backend novo envia null pra super-admin; backend velho
    // não envia → cai pra null + isSuperAdmin computado por fallback.
    profile: me.profile ?? null,
    permissions: me.permissions ?? fallbackPermissions,
    isSuperAdmin: me.isSuperAdmin ?? (apiUser.isAdmin && !me.profile),
    ehLider: apiUser.ehLider ?? false,
    podeAbrirRh: apiUser.podeAbrirRh ?? false,
    tipo: apiUser.tipo ?? 'interno',
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const applyMe = useCallback((me: MeResponse) => {
    const adapted = adapt(me);
    setUserState(adapted);
    setConfig(me.config);
    setSentryUser({ id: adapted.id, email: adapted.email });
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/api/v1/auth/me');
      applyMe(me);
    } catch (err) {
      // 401 → token inválido/expirado → limpa state
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setAuthToken(null);
        localStorage.removeItem(REFRESH_KEY);
        setUserState(null);
      }
      // Outros erros: deixa user como tá, mas logga via sentry (já feito pelo api.ts)
    }
  }, [applyMe]);

  // Boot: se tem token, tenta validar; senão, busca apenas config pública
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        await fetchMe();
      }
      setLoading(false);
    })();

    function handleUnauthorized() {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      setUserState(null);
      clearSentryUser();
    }
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [fetchMe]);

  const loginDev = useCallback<AuthContextValue['loginDev']>(
    async ({ email, nome }) => {
      const data = await api.post<LoginResponse>(
        '/api/v1/auth/dev-login',
        { email, nome },
        { skipAuth: true }
      );
      setAuthToken(data.accessToken);
      localStorage.setItem(REFRESH_KEY, data.refreshToken);
      await fetchMe(); // pega departamentos
    },
    [fetchMe]
  );

  const loginWithGoogle = useCallback<AuthContextValue['loginWithGoogle']>(
    async (credential) => {
      const data = await api.post<LoginResponse>(
        '/api/v1/auth/google-login',
        { credential },
        { skipAuth: true }
      );
      setAuthToken(data.accessToken);
      localStorage.setItem(REFRESH_KEY, data.refreshToken);
      await fetchMe();
    },
    [fetchMe]
  );

  const entrarComSessao = useCallback<AuthContextValue['entrarComSessao']>(
    async (sessao) => {
      setAuthToken(sessao.accessToken);
      localStorage.setItem(REFRESH_KEY, sessao.refreshToken);
      await fetchMe();
    },
    [fetchMe]
  );

  const logout = useCallback<AuthContextValue['logout']>(async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    try {
      await api.post('/api/v1/auth/logout', { refreshToken });
    } catch {
      // Ignora — vamos limpar local de qualquer forma
    }
    setAuthToken(null);
    localStorage.removeItem(REFRESH_KEY);
    setUserState(null);
    clearSentryUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        config,
        isAuthenticated: !!user,
        loading,
        loginDev,
        loginWithGoogle,
        entrarComSessao,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  }
  return ctx;
}

/**
 * O mesmo contexto, sem lançar quando não há `<AuthProvider>` em volta (25/10/2026): para provedores que também vivem
 * montados sozinhos (o do menu lateral, nos testes dele) e só precisam saber, QUANDO houver login, se é o técnico de
 * campo. Fora do provedor devolve `null` — quem chama decide o que fazer.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthSePuder(): AuthContextValue | null {
  return useContext(AuthContext);
}
